/**
 * Dashboard widget registry. Each widget is a self-contained card that reads
 * from a shared data context (the dashboard fetches the sources once and passes
 * them in). The dashboard page handles which widgets show, their order, and
 * drag-to-reorder; this file just defines the catalog + how each renders.
 */

import { useQuery } from "@tanstack/react-query";
import { Card, CardHead, moneyFmt } from "../components/cards.js";
import { MetricCard } from "../components/MetricCard.js";
import { RankedBars } from "../components/RankedBars.js";
import { TrendChart } from "../components/TrendChart.js";
import { ChartTypeMenu, useChartPref } from "../components/ChartTypeMenu.js";
import { Spark, MiniTable, useMetricTrend, CARD_CHART_MENU, cardChartType } from "../components/CardTrend.js";
import { EquationStrip } from "../components/ExecStory.js";
import { ProfitModelSection } from "../pages/ProfitModelPage.js";
import { useApiClient } from "./api-context.js";
import type { Capabilities } from "./card-requirements.js";
import type { DrillRequest, DrillMetric, DrillWindow } from "../components/DrillExplorer.js";
import { windowDaysForKey, type PeriodKey } from "./period.js";
import { RevenueCmToggle, useStickyRevenueCmMode } from "../components/RevenueCmToggle.js";
import { DataTrustCardNotice } from "./data-trust-banner.js";
import { currentPeriodData } from "./period-query-data.js";

export interface WidgetCtx {
  caps?: Capabilities;
  openDrill?: (req: DrillRequest) => void;
  /** Router navigate — for cards whose "drill" is a dedicated page (LTV). */
  goTo?: (path: string) => void;
  periodKey?: PeriodKey;
  periodLabel?: string;
  /** Custom-range-aware period plumbing (#3) — so widget queries/drills follow a
   *  custom calendar range instead of collapsing to 30d. */
  periodQuery?: { period?: string; from?: string; to?: string };
  periodCacheKey?: string;
  periodWindowDays?: number | null;
  periodDrillWindow?: DrillWindow;
  retentionLoading?: boolean;
  productsLoading?: boolean;
  series?: Array<{ date: string; revenue: number; cm: number | null; orders: number }>;
  overview?: {
    current: { revenueBase: number; revenueGrossBase: number; cmBase: number; cmPercent: number | null; orderCount: number; customerCount: number; newCustomerCount: number };
    changes: { revenuePct: number | null; cmPct: number | null; orderCountPct: number | null; customerCountPct: number | null };
  };
  /** Company-wide gross revenue (ignores the page product filter) — the MER
   *  widget's numerator, so MER stays whole-company against company-wide spend. */
  revenueGrossCompany?: number;
  /** Company-wide Revenue/CM on the acquisition-spend-covered slice. Equals
   *  overviewCompany for normal windows and starts at cost tracking for an
   *  older All-time/custom window. */
  acquisitionOverview?: WidgetCtx["overview"];
  acquisitionRevenueGross?: number;
  acquisitionCoverageNote?: string | null;
  /** Company-wide overview (ignores the page product filter) — the profit-equation
   *  waterfall reads this so its Revenue + CM stages sit on the SAME whole-company
   *  population as its Marketing + Overhead stages. Reading the product-filtered
   *  `overview` there made one product carry all company marketing/overhead → a fake
   *  loss when a product was selected (QA fix 2026-07-15). */
  overviewCompany?: WidgetCtx["overview"];
  cac?: {
    blendedCac: number | null;
    totalNewCustomers: number;
    /** WHOLE-BUSINESS marketing spend (all commissions + ad spend) — the Exec MER
     *  widget + profit-equation strip are whole-business, so they read this, not
     *  the acquisition-only total. */
    totalMarketingSpendBase: number;
    ltvCacRatio: number | null;
  };
  retention?: {
    summary: { repeatRate: number | null; repeatRatePriorLens?: number | null; repeatCohortNote?: string; repeatHorizonDays: number | null; ordersPerCustomer: number; avgDaysToSecond: number | null; refundRatePct: number | null };
    newVsReturning: Array<{ month: string; incomplete?: boolean; newCustomers: number; returningCustomers: number; newRev: number; returningRev: number }>;
    timeBetween: Array<{ step: string; avgDays: number; customers: number }>;
  };
  ltv?: { overview: { averageByTimeframe: Array<{ timeframeDays: number; averageLtvBase: number; cohortSize: number }> } };
  geo?: { states: Array<{ state: string; revenue: number; customers: number; valuePerCustomer: number }> };
  products?: { products: Array<{ productName: string; groupName?: string; skuCode: string; revenue: number; units: number; cm?: number; lastPrice: number | null }> };
  /** Subset of /dashboard/profit-model (deduped with the Profit Model section). */
  profitModel?: {
    operationalDrag: { value: number | null; status: "live" | "needs_setup"; sample?: number };
    windowOverhead: number | null;
    overheadHistory: {
      recordedMonths: number;
      sparse: boolean;
      trust: "none" | "manual" | "accounting_pending" | "accounting_approved";
      fallbackMonths: number;
    };
    financialWindow: {
      days: number;
      note: string | null;
      revenueBase: number;
      revenueGrossBase: number;
      cmBase: number;
      marketingSpendBase: number;
      overheadBase: number | null;
    } | null;
  };
  /** Monthly overhead (base currency) — the profit-equation widget's last stage. */
  overhead?: number | null;
  /** "% of the way to full break-even" (profit basis) for the equation strip's
   *  shared-derivation line — the SAME value the flagship Capital-Return-Velocity
   *  card shows. null when the window has broken even / is unmeasurable. */
  pctToBreakEven?: number | null;
}

export type WidgetSize = "kpi" | "half" | "wide";
export interface WidgetDef {
  id: string;
  title: string;
  size: WidgetSize;
  render: (ctx: WidgetCtx) => React.ReactNode;
}

const pct = (n: number | null | undefined): string => (n === null || n === undefined ? "—" : `${(n * 100).toFixed(1)}%`);
// The drill inherits the page's period keyword; the server resolves it to the
// SAME window the card used, so the drill total reconciles exactly.
const drillKpi = (c: WidgetCtx, title: string, metric: DrillMetric): (() => void) | undefined =>
  c.openDrill
    ? () => c.openDrill?.({
        title: `${title} · ${(c.periodLabel ?? "Last 30 days").toLowerCase()}`,
        metric,
        dimension: "month",
        period: c.periodDrillWindow ?? { key: c.periodKey ?? "30d", label: c.periodLabel ?? "Last 30 days" },
      })
    : undefined;

// Blended ratios (MER/aMER/CAC) drill into the RATIO over time (RatioDrill),
// not a one-sided proxy — clicking MER shows MER, not the revenue numerator
// (founder 2026-06-14). metric is a placeholder the ratio drill ignores.
const drillRatio = (c: WidgetCtx, title: string, ratio: "mer" | "amer" | "cac" | "cm-mer"): (() => void) | undefined =>
  c.openDrill
    ? () => c.openDrill?.({
        title: `${title} · ${(c.periodLabel ?? "Last 30 days").toLowerCase()}`,
        metric: "revenue",
        ratio,
        period: c.periodDrillWindow ?? { key: c.periodKey ?? "30d", label: c.periodLabel ?? "Last 30 days" },
      })
    : undefined;

/**
 * Day-parting strip (Jeremy 2026-06-10): Morning/Afternoon/Evening/Night
 * split of the card's metric, in the ACCOUNT'S OWN timezone (each client
 * sets theirs in Settings — the backend buckets by accounts.time_zone).
 * Click a segment to open the full Time-of-day drill.
 */
const PART_SHORT: Array<{ match: string; label: string }> = [
  { match: "Morning", label: "Morn" },
  { match: "Afternoon", label: "Aft" },
  { match: "Evening", label: "Eve" },
  { match: "Night", label: "Night" },
];

function DaypartStrip({ ctx, metric, money }: { ctx: WidgetCtx; metric: DrillMetric; money: boolean }): React.ReactElement | null {
  const api = useApiClient();
  const periodParams = ctx.periodQuery ?? { period: ctx.periodKey ?? "30d" };
  const query = useQuery({
    queryKey: ["daypart", metric, ctx.periodCacheKey ?? ctx.periodKey ?? "30d"],
    queryFn: () => api.get<{ rows: Array<{ key: string; value: number }> }>("/dashboard/breakdown", {
      metric, dimension: "time_of_day", ...periodParams,
    }),
    staleTime: 5 * 60_000,
  });
  const rows = currentPeriodData(query.data, query.isPlaceholderData)?.rows ?? [];
  if (rows.length === 0) return null;
  const max = Math.max(1, ...rows.map((r) => r.value));
  const fmt = (v: number): string =>
    money ? moneyFmt(v) : Math.round(v).toLocaleString();
  return (
    <div style={{ display: "flex", gap: 6, marginTop: 9 }}
      title="By time of day (your store's timezone) — click to explore">
      {PART_SHORT.map(({ match, label }) => {
        const row = rows.find((r) => r.key.startsWith(match));
        const v = row?.value ?? 0;
        return (
          <div key={match}
            onClick={(e) => {
              e.stopPropagation();
              ctx.openDrill?.({
                title: `${METRIC_LABELS[metric] ?? metric} by time of day`,
                metric, dimension: "time_of_day",
                period: ctx.periodDrillWindow ?? { key: ctx.periodKey ?? "30d", label: ctx.periodLabel ?? "Last 30 days" },
              });
            }}
            style={{ flex: 1, cursor: "pointer", minWidth: 0 }}>
            <div style={{ height: 22, background: "var(--surface-2)", borderRadius: 4, display: "flex", alignItems: "flex-end", overflow: "hidden" }}>
              <div style={{ width: "100%", height: `${Math.max(8, (v / max) * 100)}%`, background: "var(--accent)", opacity: 0.75, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 9, color: "var(--faint)", marginTop: 2, textAlign: "center", whiteSpace: "nowrap" }}>{label}</div>
            <div className="dd-num" style={{ fontSize: 9.5, color: "var(--num)", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fmt(v)}</div>
          </div>
        );
      })}
    </div>
  );
}

const METRIC_LABELS: Partial<Record<DrillMetric, string>> = {
  revenue: "Revenue", cm: "Contribution margin", orders: "Orders", customers: "Customers", aov: "Avg order value",
};

// Spark / MiniTable / the chart-type menu now live in components/CardTrend.tsx
// so EVERY card (not just KPIs) can render the same trend block. See imports.
type KpiView = "number" | "line" | "area" | "bars" | "table";

/** KPI card: headline number always on top; a COMPACT chart-type DROPDOWN
 *  (founder 2026-06-13: dropdown, not pills) switches the trend below between
 *  Number / Line / Area / Bars / Table, from the daily series, with a rolling-
 *  average overlay (on by default). Choice persists per browser. */
function Kpi({ title, value, delta, sub, onClick, trend, trendKey, trendMetric, money = true, daypart, explain, headerExtra }: {
  title: string; value: string; delta?: number | null; sub?: string; onClick?: () => void;
  trend?: Array<{ label: string; value: number }>; trendKey?: string;
  /** When no explicit `trend` is passed, fetch the series for this metric from
   *  the period-aware breakdown endpoint — so EVERY KPI can chart, not just the
   *  ones that happen to ride the overview daily series (founder 2026-06-13). */
  trendMetric?: DrillMetric; money?: boolean; daypart?: React.ReactNode;
  /** ⓘ tooltip — the What it is / How it's calculated / Example / Note lines
   *  (founder 2026-06-29: EVERY card gets the full-doc tooltip, no exceptions). */
  explain?: string[];
  headerExtra?: React.ReactNode;
}): React.ReactElement {
  const fetched = useMetricTrend(trendMetric, !trend && !!trendMetric);
  const tr = trend ?? fetched;
  const key = trendKey ?? trendMetric;
  const hasTrend = !!tr && tr.length >= 3 && !!key;
  // kpi- prefix keeps these keys distinct from the full TrendChart cards.
  // Default to the LINE trend so the rolling-average is visible out of the box
  // (founder: "rolling averages by default"); Number is one dropdown-click away.
  const pref = useChartPref(key ? `kpi-${key}` : undefined, "line");
  const view = (hasTrend ? cardChartType(pref.type) : "number") as KpiView;
  const isChart = view === "line" || view === "area" || view === "bars";
  const fmt = (n: number): string => (money ? moneyFmt(n) : Math.round(n).toLocaleString());
  return (
    <Card onClick={onClick} style={{ marginBottom: 0, height: "100%", cursor: onClick ? "pointer" : "default" }}>
      {/* The drill ↗ is rendered by CardHead right after the ⓘ (founder 2026-07-06),
          so the chart-type menu keeps the top-right corner to itself — no clash. */}
      <CardHead title={title} explain={explain}>
        {headerExtra}
        {hasTrend && <ChartTypeMenu value={view} options={CARD_CHART_MENU} onChange={pref.setType} avg={pref.avg} onToggleAvg={pref.setAvg} avgApplies={isChart} />}
      </CardHead>
      <div className="dd-num" style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700, marginTop: 9, color: "var(--num)" }}>{value}</div>
      <div style={{ display: "flex", gap: 9, marginTop: 7, alignItems: "center" }}>
        {delta !== null && delta !== undefined && (
          <span className="dd-num" style={{ fontSize: 12.5, fontWeight: 700, color: delta >= 0 ? "var(--good)" : "var(--bad)" }}>
            {delta >= 0 ? "▲" : "▼"} {Math.abs(delta * 100).toFixed(1)}%
          </span>
        )}
        {sub && <span style={{ color: "var(--body)", fontSize: 11.5 }}>{sub}</span>}
      </div>
      {hasTrend && tr && view === "table" && <MiniTable trend={tr} fmt={fmt} />}
      {hasTrend && tr && isChart && <Spark values={tr.map((t) => t.value)} kind={view} avg={pref.avg} />}
      {daypart}
    </Card>
  );
}





// Profit Efficiency Ratio (PER) card was REMOVED from the UI 2026-06-16 (founder:
// "only include it if genuinely useful"). PER = product CM% ÷ company CM%, so on a
// flat sample CM% it's mathematically 1.0 for EVERY product (revenue cancels in
// the ratio) — useless until REAL per-product CM exists. It returns as a
// full-detail table (revenue · %rev · merch · refunds · delivery$ · delivery% ·
// CM% · CM$ · %toCM · PER, no category grouping) as a payoff of the CM onboarding,
// where the spread is real (driven by per-product delivery/fulfillment cost). The
// backend services/metrics/profit-efficiency.ts + GET /dashboard/profit-efficiency
// are left in place for that rebuild. (memory: profit-efficiency-ratio.)

export const WIDGETS: WidgetDef[] = [
  // Full-width story blocks — now movable/hideable like any card (founder 2026-06-30).
  { id: "equation-strip", title: "Profit equation", size: "wide", render: (c) => (
    // Revenue + CM read the COMPANY-WIDE overview (not the product-filtered one) so
    // all four stages share one whole-company population — Marketing (c.cac) and
    // Overhead are already company-wide. Picking a product no longer narrows only
    // revenue/CM and leaves this product carrying the whole company's marketing +
    // overhead (a fake loss). Matches the note "a product filter won't move it".
    <EquationStrip
      windowDays={c.profitModel?.financialWindow?.days ?? c.periodWindowDays ?? windowDaysForKey(c.periodKey ?? "30d")}
      periodLabel={c.periodLabel ?? "this period"}
      revenue={c.profitModel?.financialWindow?.revenueBase}
      cm={c.caps?.contributionMargin && c.profitModel?.financialWindow ? c.profitModel.financialWindow.cmBase : null}
      spend={c.caps?.marketingSpend && c.profitModel?.financialWindow ? c.profitModel.financialWindow.marketingSpendBase : null}
      windowOverhead={c.profitModel?.financialWindow?.overheadBase ?? null}
      basisNote={c.profitModel?.financialWindow?.note}
      overheadHistory={c.profitModel?.overheadHistory}
      caps={c.caps}
      pctToBreakEven={c.pctToBreakEven ?? null}
    />
  ) },
  { id: "profit-model", title: "Profit model (5 levers)", size: "wide", render: () => <ProfitModelSection /> },
  // ── Profit-model levers (Exec) — live where we can, locked notice otherwise.
  // (The standalone "Contribution margin" card was removed 2026-06-14 — founder:
  // "I don't think that's really a card to see." CM stays foundational under the
  // hood: it still powers the profit-model waterfall, CM-MER, breakeven, LTV:CAC,
  // and the new Profit Efficiency Ratio card.)
  { id: "mer", title: "MER (blended)", size: "kpi", render: (c) => <ExecMerCard c={c} /> },
  { id: "cac", title: "Acquisition (CAC)", size: "kpi", render: (c) => <MetricCard title={`Acquisition (CAC) · ${c.periodLabel ?? "Last 30 days"}`} trustWarning={<DataTrustCardNotice card="CAC" />} companyWide requires={["marketingSpend"]} caps={c.caps} value={c.cac?.blendedCac !== null && c.cac?.blendedCac !== undefined ? moneyFmt(c.cac.blendedCac) : undefined} sub={c.cac ? `${c.cac.totalNewCustomers.toLocaleString()} new customers${c.acquisitionCoverageNote ? ` · ${c.acquisitionCoverageNote}` : ""}` : undefined} sample={{ value: "$24.80", sub: "486 new customers" }} trendRatio="cac" chartKey="cac" onClick={drillRatio(c, "CAC", "cac")} explain={["<b>What it is:</b> Blended cost to acquire one new customer. It spreads acquisition spend across every brand-new customer in the same dates.", "<b>How it's calculated:</b> Ad spend plus affiliate commissions on first orders, divided by new customers acquired during those same dates.", "<b>Example (sample data · year-to-date):</b> $273,304 acquisition spend ÷ 5,065 new customers = $53.96.", "<b>Note:</b> Blended, so a product filter will not move it. If your dates reach back before cost tracking began, both spend and customers start at the first covered month. Missing spend never counts as $0."]} /> },
  // (The standalone "Cash velocity (CRV)" KPI was removed 2026-06-16 — founder:
  // "can be removed since it was added above." Capital Return Velocity now lives
  // in the Profit Model section as its own lever tile + the Profit Inflection
  // Point hero, with the real inventory + payment-terms math behind it.)
  { id: "op-drag", title: "Operational drag", size: "kpi", render: (c) => {
    const od = c.profitModel?.operationalDrag;
    const live = od?.status === "live" && od.value !== null && od.value !== undefined;
    return <MetricCard title={`Operational drag · ${c.periodLabel ?? "Last 30 days"}`} trustWarning={<DataTrustCardNotice card="Operational drag" />} companyWide requires={["overhead", "contributionMargin"]} caps={c.caps} value={live ? `${od!.value!.toFixed(2)}×` : undefined} sub={live ? "overhead ÷ contribution margin" : undefined} sample={{ value: "0.38×", sub: "overhead ÷ contribution margin" }} explain={["<b>What it is:</b> How much of your contribution margin gets eaten by fixed overhead. Lower is better.", "<b>How it's calculated:</b> Overhead across the selected dates ÷ contribution margin from those same dates. Complete accounting months use reviewed actual expenses. Missing months use the approved planning amount.", "<b>Example (sample data):</b> $132,000 overhead ÷ $611,997 margin = 0.22× (22% of your margin goes to overhead).", "<b>Note:</b> Blended. It needs overhead and product costs. Any planning fallback is shown on the card."]} />;
  } },
  // (The "CM generated" KPI was removed 2026-06-16 — founder: "not sure it adds
  // value." It's redundant: the "Your profit model" waterfall already shows CM
  // with its % of revenue, and the Profit Model CM lever shows CM%.)
  // Loading/error shows "—", never a fake $0 (a founder seeing "Revenue $0" at
  // 7am must mean the business made nothing, not that a fetch was in flight).
  { id: "revenue", title: "Revenue", size: "kpi", render: (c) => <ExecRevenueCard c={c} /> },
  { id: "orders", title: "Orders", size: "kpi", render: (c) => <Kpi title={`Orders · ${c.periodLabel ?? "Last 30 days"}`} value={c.overview ? c.overview.current.orderCount.toLocaleString() : "—"} delta={c.overview?.changes.orderCountPct} sub="vs prior period" trend={c.series?.map((d) => ({ label: d.date, value: d.orders }))} trendKey="orders" money={false} daypart={<DaypartStrip ctx={c} metric="orders" money={false} />} onClick={drillKpi(c, "Orders", "orders")} explain={["<b>What it is:</b> How many orders were placed in the date range. The little arrow compares it to the same-length period just before.", "<b>How it's calculated:</b> A count of every order dated inside the window.", "<b>Example (sample data · year-to-date):</b> 15,018 orders. “▼ 1.7%” means 1.7% fewer than the period before.", "<b>Note:</b> Narrows with the product filter — pick a product and this counts only orders that included it as a paid item (a free or BOGO add-on of that product doesn't trigger the filter). Includes refunded orders (a refund takes the money back, not the order). The little bars below split orders by time of day (your store's timezone)."]} /> },
  // "Customers" = ALL customers who bought in the window (new + returning), not
  // just new ones — so we show the split (founder 2026-06-16: "is that NEW
  // customers? label it"). New = first order in the window; returning = the rest.
  { id: "customers", title: "Customers", size: "kpi", render: (c) => <Kpi title={`Customers · ${c.periodLabel ?? "Last 30 days"}`} value={c.overview ? c.overview.current.customerCount.toLocaleString() : "—"} delta={c.overview?.changes.customerCountPct} sub={c.overview ? `${c.overview.current.newCustomerCount.toLocaleString()} new · ${Math.max(0, c.overview.current.customerCount - c.overview.current.newCustomerCount).toLocaleString()} returning` : undefined} trendMetric="customers" money={false} daypart={<DaypartStrip ctx={c} metric="customers" money={false} />} onClick={drillKpi(c, "Customers", "customers")} explain={["<b>What it is:</b> How many different customers bought in the range, split into brand-new vs returning.", "<b>How it's calculated:</b> A count of distinct customers with an order in the window; “new” = their first-ever order falls in the window, “returning” = everyone else. With a product selected, the first-ever order must include that product.", "<b>Example (sample data · year-to-date):</b> 7,342 customers — 5,065 new + 2,277 returning.", "<b>Note:</b> Pick a product and this counts customers who bought it in the window. Someone who first bought something else and added this product later counts as returning. A customer with several IDs counts once. Refunds don't remove a customer."]} /> },
  { id: "repeat", title: "Repeat rate", size: "kpi", render: (c) => { const h = c.retention?.summary.repeatHorizonDays; const note = c.retention?.summary.repeatCohortNote; const prior = c.retention?.summary.repeatRatePriorLens; return <Kpi title="Repeat rate" value={pct(c.retention?.summary.repeatRate)} sub={h ? `bought again within ${h} days` : "bought 2+ times"} explain={["<b>What it is:</b> The share of one fully matured customer group that bought again within the selected runway.", `<b>How it's calculated:</b> Standard date choices use the newest group that has fully completed the selected horizon. Custom dates keep the acquisition dates you chose and include only customers who completed the full runway.${note ? ` The group shown is ${note}.` : ""} At All time, it is 2+ orders ever among customers at least 30 days old.`, "<b>Example:</b> A 1-year pick shows customers acquired 12 to 24 months ago and how many bought again within their first year.", `<b>Note:</b> Narrows with the product filter, keeping customers whose first order included that product. The older any-era lens${prior !== null && prior !== undefined ? ` reads ${pct(prior)}` : " is not available yet"}. The card uses one completed cohort because it is the cleaner decision basis.`]} />; } },
  { id: "refund", title: "Refund rate", size: "kpi", render: (c) => <Kpi title="Refund rate" value={pct(c.retention?.summary.refundRatePct)} sub={`of orders · ${(c.periodLabel ?? "this period").toLowerCase()}`} trendMetric="refund_rate" money={false} onClick={c.openDrill ? () => c.openDrill?.({ title: "Refund rate", metric: "refund_rate", dimension: "month" }) : undefined} explain={["<b>What it is:</b> The share of the orders in your selected date range that got refunded — a quick read on product or fulfillment problems.", "<b>How it's calculated:</b> Refunded orders ÷ total orders, for the date range you pick at the top.", "<b>Example (sample data · year-to-date):</b> about 4.5% of orders are refunded.", "<b>Note:</b> Narrows with the product filter — it counts only orders containing that product. Moves with the date picker now (it used to be pinned to all-time). This counts ORDERS, not refunded dollars — and for the full refund timing breakdown, see the Operations page."]} /> },
  {
    id: "revenue-trend", title: "Revenue by month", size: "half",
    render: (c) => <ExecRevenueTrendCard c={c} />,
  },
  {
    id: "top-products", title: "Top products", size: "half",
    render: (c) => <TopProductsCard c={c} />,
  },
  {
    id: "time-between", title: "Time between purchases", size: "half",
    render: (c) => {
      const horizon = c.retention?.summary.repeatHorizonDays ?? null;
      const cohortNote = c.retention?.summary.repeatCohortNote;
      return (
        <Card>
          <CardHead title="Time between purchases" explain={[
            "<b>What it is:</b> The average wait between each order for customers who reached that next order. Smaller gaps at later steps mean the buying habit is getting stronger.",
            horizon && cohortNote
              ? `<b>How it's calculated:</b> It uses the same completed customer group as Repeat rate: ${cohortNote}. We count orders reached inside each customer's first ${horizon} days, then average the wait at each step.`
              : "<b>How it's calculated:</b> It uses customers at least 30 days old and their full known order history, then averages the wait at each step among the people who reached it.",
            "<b>Example:</b> If 400 customers reached order two after an average of 52 days, the first bar shows 52 days. The later bars usually include fewer customers because fewer people reach those orders.",
            "<b>Note:</b> Narrows with the product filter, keeping only customers whose FIRST order included the product. The 1st-to-2nd bar is the old Days to 2nd order number, now folded into this one card.",
          ]} />
          <RankedBars loading={c.retentionLoading} rows={(c.retention?.timeBetween ?? []).map((t) => ({ key: `Order ${t.step}`, value: t.avgDays }))} fmt={(v) => `${Math.round(v)} days`} money={false} />
        </Card>
      );
    },
  },
];

function cmReady(c: WidgetCtx): boolean {
  return Boolean(c.caps?.financialBasis === "profit" && c.caps.contributionMargin && !c.caps.cmGap);
}

function ExecRevenueCard({ c }: { c: WidgetCtx }): React.ReactElement {
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-exec-revenue");
  const hasCm = cmReady(c);
  const cm = mode === "profit" && hasCm;
  const metric: DrillMetric = cm ? "cm" : "revenue";
  const trend = c.series?.flatMap((day) => {
    const value = cm ? day.cm : day.revenue;
    return value === null ? [] : [{ label: day.date, value }];
  });
  return (
    <Kpi
      title={`Revenue · ${c.periodLabel ?? "Last 30 days"}`}
      value={c.overview ? moneyFmt(cm ? c.overview.current.cmBase : c.overview.current.revenueBase) : "—"}
      delta={cm ? c.overview?.changes.cmPct : c.overview?.changes.revenuePct}
      sub={cm ? "contribution margin vs prior period" : "revenue vs prior period"}
      trend={trend}
      trendKey={cm ? "exec-cm" : "exec-revenue"}
      money
      daypart={<DaypartStrip ctx={c} metric={metric} money />}
      onClick={drillKpi(c, cm ? "Contribution margin" : "Revenue", metric)}
      headerExtra={<RevenueCmToggle mode={cm ? "profit" : "revenue"} setMode={setMode} cmDisabled={!hasCm} />}
      explain={[
        "<b>What it is:</b> The money your sales brought in, or the contribution margin (CM) left after the variable costs tied to those same sales.",
        "<b>How it's calculated:</b> Revenue adds the selected orders after refunds and sales tax. CM uses those exact orders and dates, then subtracts each product's own variable costs. If your store gives us one refund total for the whole order, each view subtracts only the part that belongs to the sales dollars it counts.",
        "<b>Example (sample data · year-to-date):</b> Revenue is $1,117,474. CM is $611,997 after the costs to make, ship, and process those sales.",
        "<b>Note:</b> Pick a product to narrow both views. CM turns on after every selling product has costs. Marketing Efficiency Ratio uses gross revenue, so its Revenue view can be slightly higher.",
      ]}
    />
  );
}

function ExecMerCard({ c }: { c: WidgetCtx }): React.ReactElement {
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-exec-mer");
  const hasCm = cmReady(c);
  const cm = mode === "profit" && hasCm;
  const spend = c.cac?.totalMarketingSpendBase ?? 0;
  const numerator = cm ? c.acquisitionOverview?.current.cmBase ?? 0 : c.acquisitionRevenueGross ?? 0;
  const value = spend > 0 && numerator > 0 ? `${(numerator / spend).toFixed(2)}×` : undefined;
  return (
    <MetricCard
      title={`MER (blended) · ${c.periodLabel ?? "Last 30 days"}`}
      trustWarning={<DataTrustCardNotice card="MER" />}
      companyWide
      requires={cm ? ["contributionMargin", "marketingSpend"] : ["marketingSpend"]}
      caps={c.caps}
      headerExtra={<RevenueCmToggle mode={cm ? "profit" : "revenue"} setMode={setMode} cmDisabled={!hasCm} />}
      value={value}
      sub={spend > 0 ? `${cm ? "contribution margin ÷ all marketing spend" : "revenue ÷ all marketing spend"}${c.acquisitionCoverageNote ? ` · ${c.acquisitionCoverageNote}` : ""}` : undefined}
      sample={{ value: cm ? "2.2×" : "4.2×", sub: cm ? "contribution margin ÷ all marketing spend" : "revenue ÷ all marketing spend" }}
      trendRatio={cm ? "cm-mer" : "mer"}
      chartKey={cm ? "exec-cm-mer" : "exec-mer"}
      onClick={drillRatio(c, cm ? "CM-MER" : "MER", cm ? "cm-mer" : "mer")}
      explain={[
        "<b>What it is:</b> Marketing Efficiency Ratio (MER): how much your whole business brings back for every $1 spent on marketing. Revenue shows sales. CM shows what those same sales kept after variable costs.",
        "<b>How it's calculated:</b> Revenue or contribution margin divided by the same total marketing spend. Only the top of the ratio changes.",
        "<b>Example (sample data · year-to-date):</b> Revenue MER is about 4.2×. CM-MER is about 2.2× after variable costs.",
        "<b>Note:</b> Blended. A product filter does not move it because marketing spend is not tracked by product. If your dates reach back before cost tracking began, revenue, contribution margin, and spend all start at the first covered month. Missing spend never counts as $0.",
      ]}
    />
  );
}

function ExecRevenueTrendCard({ c }: { c: WidgetCtx }): React.ReactElement {
  const api = useApiClient();
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-exec-revenue-trend");
  const hasCm = cmReady(c);
  const cm = mode === "profit" && hasCm;
  const metric: DrillMetric = cm ? "cm" : "revenue";
  const trend = useQuery({
    queryKey: ["exec-revenue-cm-trend", c.periodCacheKey ?? c.periodKey ?? "30d", metric],
    queryFn: () => api.get<{ rows: Array<{ key: string; value: number }> }>("/dashboard/breakdown", {
      metric,
      dimension: "month",
      ...(c.periodQuery ?? { period: c.periodKey ?? "30d" }),
    }),
  });
  const trendData = currentPeriodData(trend.data, trend.isPlaceholderData);
  return (
    <Card onClick={drillKpi(c, cm ? "Contribution margin by month" : "Revenue by month", metric)}>
      <CardHead
        title={`${cm ? "Contribution margin" : "Revenue"} by month`}
        explain={[
          "<b>What it is:</b> Your revenue or contribution margin drawn month by month, so you can see the real direction and seasonality.",
          "<b>How it's calculated:</b> Each month's selected orders are added on the same basis as the headline card. Revenue shows sales after your chosen adjustments. CM uses those same orders and subtracts their variable costs.",
          "<b>Example (sample data · year-to-date):</b> Revenue averages about $186,000 a month. The CM view shows the smaller amount that remains after variable costs.",
          "<b>Note:</b> The current month is still filling in, so it will usually look low. Pick a product to narrow both views.",
        ]}
      >
        <RevenueCmToggle mode={cm ? "profit" : "revenue"} setMode={setMode} cmDisabled={!hasCm} />
      </CardHead>
      <div style={{ marginTop: 10 }}>
        <TrendChart loading={trend.isLoading || trend.isPlaceholderData} kind="line" switchKinds={["line", "area", "step", "stacked-bars", "table", "number"]} chartKey={cm ? "cm-trend" : "revenue-trend"} points={(trendData?.rows ?? []).map((row) => ({ label: row.key, a: row.value }))} />
      </div>
    </Card>
  );
}

/** Top products — rolls per-SKU rows up to their FAMILY (the product_groups
 *  overlay built during onboarding, e.g. every "Collagen" SKU lumps into one
 *  bar; unclassified SKUs land in 'Ungrouped') and lets the CEO rank by revenue
 *  or by profit (CM). Clicking a family drills into the products/SKUs within it.
 *  The Profit option only appears once costs are built (otherwise every CM is 0). */
function TopProductsCard({ c }: { c: WidgetCtx }): React.ReactElement {
  const [rank, setRank] = useStickyRevenueCmMode("dd-mode-exec-top-products");
  const byFamily = new Map<string, { revenue: number; cm: number; units: number }>();
  for (const p of c.products?.products ?? []) {
    const family = p.groupName ?? p.productName;
    const cur = byFamily.get(family) ?? { revenue: 0, cm: 0, units: 0 };
    cur.revenue += p.revenue;
    cur.cm += p.cm ?? 0;
    cur.units += p.units;
    byFamily.set(family, cur);
  }
  const products = Array.from(byFamily, ([name, v]) => ({ name, ...v }));
  const hasCm = cmReady(c);
  const quantityReady = c.caps?.physicalQuantity === true;
  const byProfit = rank === "profit" && hasCm;
  const rows = products
    .sort((a, b) => (byProfit ? b.cm - a.cm : b.revenue - a.revenue))
    .slice(0, 6)
    .map((p) => ({
      key: p.name,
      value: byProfit ? p.cm : p.revenue,
      ...(quantityReady ? { sub: `${p.units.toLocaleString()} packs sold` } : {}),
    }));
  return (
    <Card>
      <CardHead title={`Top products by ${byProfit ? "CM" : "revenue"} · ${c.periodLabel ?? "Last 30 days"}`}
        explain={["<b>What it is:</b> Your biggest products ranked by revenue or by contribution margin (CM). Click a slice to dig into the product.", "<b>How it's calculated:</b> Each SKU's sales or CM are rolled up to its product, then the top 6 products are ranked. The same products and dates are used in both views.", "<b>Example (sample data · year-to-date, Revenue):</b> Daily Greens $436,318, Deep Sleep $254,781, Radiant Collagen $133,025, Omega Gold $77,735, Gut Guardian Probiotic $76,104, The Wellness Reset $59,220.", "<b>Note:</b> Shows the top 6 products only. CM turns on after you enter product costs. Revenue here is item-level, so it can differ from the headline Revenue card, which includes shipping and subtracts refunds."]}>
        <RevenueCmToggle mode={byProfit ? "profit" : "revenue"} setMode={setRank} cmDisabled={!hasCm} />
      </CardHead>
      {/* A little breathing room below the header (Revenue/CM toggle) before
          the chart (founder 2026-07-06). */}
      <div style={{ marginTop: 10 }}>
        <p style={{ color: "var(--body)", fontSize: 12, margin: "0 0 8px" }}>{byProfit ? "Top 6 products. Product margin before refunds." : "Top 6 products. Revenue before refunds, without shipping or tax."}</p>
        <RankedBars loading={c.productsLoading} donutToggle defaultView="donut" rows={rows} fmt={moneyFmt}
          onPick={c.openDrill ? (label) => c.openDrill?.({ title: label, metric: byProfit ? "product_cm" : "product_revenue", dimension: "product", filters: { productGroup: label } }) : undefined} />
      </div>
      {c.caps?.financialBasis === "profit" && <DataTrustCardNotice card="Products" />}
    </Card>
  );
}


// Exec default = a live KPI strip (Revenue, MER, CAC, CM generated, Operational
// drag, Profit efficiency) then the two big charts → the engagement band → the
// two ranked lists. The 5 profit levers (incl. Capital Return Velocity) live in
// the Profit Model section above the grid now. Changing this REQUIRES bumping
// LS_KEY in DashboardPage.tsx or saved layouts mask it.
// 2026-06-12 founder edits: "Are we on track" (revenue-trend) REMOVED from
// the default (stays in the registry — re-addable via Customize); MER added.
// 2026-06-16: "crv" KPI removed (duplicated the Profit Model section); the
// all-time "ltv365" KPI removed from the default (stays re-addable via the
// library — its blank-at-short-windows issue is fixed by the all-time query).
// Exec default (2026-06-21 founder declutter): a tight, profit-first KPI strip +
// Top products. Removed from Exec: the two LTV cards + the Lifetime-value lever
// (don't move with the date range — Exec is a date-range trend view), AOV (lives
// on Marketing), New-vs-returning (lives on Retention), Top states (population
// vanity). Orders / Refund rate / Operational drag / payback curve stay (founder
// kept them). Changing this REQUIRES bumping LS_KEY in DashboardPage.tsx.
export const DEFAULT_LAYOUT = ["equation-strip", "profit-model", "revenue", "mer", "cac", "op-drag", "orders", "customers", "repeat", "refund", "top-products", "time-between"];
